import React from 'react'
import { observer } from 'mobx-react-lite'
import TableCell from '@mui/material/TableCell'
import Checkbox from '@mui/material/Checkbox'
import { CustomTableStore, useCustomTableStore } from '../CustomTableStore'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from 'theme/useTheme'
import useCustomTableRenderLog from '../hooks/useCustomTableRenderLog'
import useStore from 'reusable/mobx-store-utils'

export const CustomTableHeaderSelectCell = observer(function CustomTableHeaderSelectCell<RowData extends AnyObject, RowStore>() {
   useCustomTableRenderLog('CustomTableHeaderSelectCell')
   const customTableStore = useCustomTableStore<CustomTableStore<RowData, RowStore>>()
   const theme = useTheme()

   const localStore = useStore(
      sp => ({
         get checkboxHeaderCellStyle() {
            return {
               position: 'sticky',
               top: customTableStore.headerCellTopValue,
               zIndex: 1,
               whiteSpace: 'nowrap',
               maxWidth: '1%',
               borderCollapse: 'separate',
               paddingLeft: theme.spacing(2),
               paddingRight: theme.spacing(2),
            } as React.CSSProperties
         },
         get checkboxData() {
            let checkboxProps = undefined
            let title = ''
            if (customTableStore.sp.showSelectAllCheckbox) {
               const selected = customTableStore.rowsSelectedCount
               const totalRowCount = customTableStore.localRows.length
               const selectedPage = customTableStore.rowsSelectedCountPage
               const totalRowCountPage = customTableStore.filteredSortedAndPagedRows.length
               const pagination = customTableStore.sp.pagination

               if (customTableStore.isRemoteData) {
                  if (selected === 0) {
                     title =
                        'No items selected. Click here to select all' +
                        (pagination ? ' from this page' : '')
                     checkboxProps = {
                        checked: false,
                        onClick: customTableStore.selectAll,
                     }
                  } else if (selected < totalRowCount) {
                     title =
                        'Some items are selected. Click here to select all' +
                        (pagination ? ' from this page' : '')
                     checkboxProps = {
                        indeterminate: true,
                        checked: true,
                        onClick: customTableStore.selectAll,
                     }
                  } else {
                     checkboxProps = {
                        checked: true,
                        onClick: customTableStore.deselectAll,
                     }
                     title = 'All items are selected. Click here to deselect all.'
                  }
               } else {
                  if (selected === 0) {
                     title =
                        'No items selected. Click here to select all' +
                        (pagination ? ' from this page' : '')
                     checkboxProps = {
                        checked: false,
                        onClick: customTableStore.selectPageAll,
                     }
                  } else if (selectedPage < totalRowCountPage) {
                     title =
                        'Some items are already selected. Click here to select all items' +
                        (pagination ? ' from this page.' : '.')
                     checkboxProps = {
                        indeterminate: true,
                        checked: true,
                        onClick: customTableStore.selectPageAll,
                     }
                  } else if (selectedPage === totalRowCountPage && selected < totalRowCount) {
                     if (pagination) {
                        title =
                           "Some items are already selected. Click here to select all pages' items."
                     } else {
                        title = 'Some items are already selected. Click here to select all items.'
                     }
                     checkboxProps = {
                        indeterminate: true,
                        checked: true,
                        onClick: customTableStore.selectAll,
                     }
                  } else {
                     checkboxProps = {
                        checked: true,
                        onClick: customTableStore.deselectAll,
                     }
                     title = 'All items are selected. Click here to deselect all.'
                  }
               }
            }
            return { checkboxProps, title }
         },
         get checkBoxStyle(): React.CSSProperties {
            return { margin: '0px', padding: '0px', zIndex: 1 }
         },
      }),
      {}
   )

   return customTableStore.sp.selectable ? (
      <TableCell variant="head" style={localStore.checkboxHeaderCellStyle} align="center">
         {localStore.checkboxData.checkboxProps ? (
            <Tooltip title={localStore.checkboxData.title}>
               <Checkbox style={localStore.checkBoxStyle} {...localStore.checkboxData.checkboxProps} />
            </Tooltip>
         ) : (
            'Selected'
         )}
      </TableCell>
   ) : null
})
